import PropTypes from 'prop-types';
import { useCallback, useMemo, memo } from 'react';

import { ClockType } from '../../api/types';
import { getUnixTime } from '../../utils/date';

import { useTimer } from './use-timer';

export function toMinutesAndSeconds(milliseconds) {
  if (milliseconds < 0) return '00:00';

  const totalSeconds = Math.round(milliseconds / 1000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor((totalSeconds - seconds) / 60);

  return [minutes, seconds].map(number => String(number).padStart(2, '0')).join(':');
}

export function toMinutes(milliseconds) {
  if (milliseconds < 0) return '00';

  const minutes = Math.floor(milliseconds / (1000 * 60));

  return String(minutes);
}

function toStoppageTime(matchTime) {
  return `${matchTime.split(':')[0]}+`;
}

function clockTimeToMilliseconds(clockTime) {
  const [minutes, seconds] = clockTime.split(':').map(Number);
  return (minutes * 60 + seconds) * 1000;
}

export function ClockCounterUI({ clock, format }) {
  const { matchTime, stoppageTime, remainingTime, remainingTimeInPeriod, stopped, clockUpdatedAt } = clock;
  const isCountUp = !remainingTime && !remainingTimeInPeriod;

  const time = useMemo(() => {
    return isCountUp
      ? getUnixTime(clockUpdatedAt) - clockTimeToMilliseconds(matchTime)
      : getUnixTime(clockUpdatedAt) + clockTimeToMilliseconds(remainingTimeInPeriod || remainingTime);
  }, [matchTime, remainingTime, remainingTimeInPeriod, clockUpdatedAt]);

  const formatter = useCallback(milliseconds => (stoppageTime ? toStoppageTime(matchTime) : format(milliseconds)), [
    format,
    stoppageTime && matchTime,
  ]);

  const displayTime = useTimer({ time, format: formatter, isStopped: stopped || stoppageTime, isCountUp });

  // There is no remainingTime or remainingTimeInPeriod, and we are increasing the time after each page reload
  // while time should be freezed. So we need to return matchTime in this case.
  return stopped && isCountUp ? matchTime : displayTime;
}

ClockCounterUI.propTypes = {
  clock: ClockType.isRequired,
  format: PropTypes.func,
};

ClockCounterUI.defaultProps = {
  format: toMinutesAndSeconds,
};

export const ClockCounter = memo(ClockCounterUI);
